import React from 'react';
import { WrappedComponentProps } from 'react-intl';
import { Form, FormProps } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import Icon, { Theme } from '../../../../components/common/icon/icon';

import { NewIconNames } from '../../../../components/common/icon/new-icon-names';
import Label from '../../../../components/common/label/label';
import { Translate } from '../../../../components/translate/translate';
import { FormSubmittingOverlay } from '../../../flows/flow-details-panel/common/form-components/form-submitting-overlay/form-submitting-overlay';
import { TeamMemberEditFormValues, TeamMembersData } from '../../team.types';
import { TeamMemberFormFields } from '../team-member-form/team-member-form-fields/team-member-form-fields';
import { TeamMemberFormFooter } from '../team-member-form/team-member-form-footer/team-member-form-footer';
import styles from './team-member-edit-form.scss';

import { Heading, Text } from '@wix/design-system';
import arrayMutators from 'final-form-arrays';
import { validateTeamMemberEditForm } from './team-member-edit-form.validation';
import { TeamMemberFormAccessPermissions } from '../../team.constants';

export interface TeamMemberEditFormOwnProps {
  onAddTeamMemberAccess(): void;
  onCancel(): void;
}

interface StateProps {
  teamMemberAvatarUrl: string;
  teamMemberEmail: string;
  teamMemberName: string;
}

interface DispatchProps {
  onSubmit(data: TeamMembersData): Promise<void>;
  openTeamMemberDeleteModal(): void;
}

export type TeamMemberEditFormProps = TeamMemberEditFormOwnProps &
  StateProps &
  DispatchProps &
  WrappedComponentProps &
  FormProps<TeamMemberEditFormValues, TeamMemberEditFormOwnProps>;

export const TeamMemberEditFormComponent: React.FunctionComponent<
  TeamMemberEditFormProps
> = ({
  intl,
  invalid,
  onSubmit,
  onAddTeamMemberAccess,
  onCancel,
  initialValues,
  openTeamMemberDeleteModal,
  pristine,
  teamMemberAvatarUrl,
  teamMemberEmail,
  teamMemberName,
}) => (
  <Form
    mutators={{ ...arrayMutators }}
    initialValues={initialValues}
    validate={(value) => {
      return validateTeamMemberEditForm(value, intl);
    }}
    onSubmit={onSubmit}
  >
    {({
      handleSubmit,
      submitting,
      invalid,
      form: {
        mutators: { pop, push },
      },
    }) => (
      <form
        className={styles.form}
        data-hook="team-member-edit-form"
        onSubmit={handleSubmit}
      >
        <FormSubmittingOverlay />

        <div className={styles.header}>
          <div className={styles.avatar}>
            <img
              alt={intl.formatMessage({
                id: 'team.edit-member-modal.form.avatar.alt',
              })}
              className={styles.avatarImg}
              data-hook="team-member-edit-img"
              src={teamMemberAvatarUrl}
            />
          </div>

          <div className={styles.nameEmailContainer}>
            <span className={styles.name} title={teamMemberName}>
              <Text dataHook="team-member-edit-name" size="small">
                {teamMemberName}
              </Text>
            </span>

            <div>
              <Heading
                size="extraTiny"
                data-hook="team-member-edit-email"
                title={teamMemberEmail}
              >
                {teamMemberEmail}
              </Heading>
            </div>
          </div>

          <Icon
            name={NewIconNames.Delete}
            onClick={openTeamMemberDeleteModal}
            theme={Theme.EmptyLink}
          />
        </div>

        <div className={styles.content}>
          <Label htmlFor="accessLevel">
            <Translate transKey="team.add-member-modal.form.access-level.title" />
          </Label>

          <FieldArray
            name="securityGroups"
            component={TeamMemberFormFields}
            rerenderOnEveryChange
          />
        </div>

        <TeamMemberFormFooter
          submitting={submitting}
          className={styles.footer}
          onAddTeamMemberAccess={() => {
            push('securityGroups', {
              level: undefined,
              permissions: TeamMemberFormAccessPermissions.Viewer,
              projectsIds: [],
            });
          }}
          onCancel={onCancel}
          invalid={pristine || invalid}
          submitButtonText={
            <Translate transKey="team.edit-member-modal.confirm-text" />
          }
        />
      </form>
    )}
  </Form>
);
